import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Search, Users, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

interface StudentRow {
  form_iv_index: string;
  full_name: string;
  class: string;
  gpa: string | null;
  overall_remark: string | null;
  student_id: string | null;
  pre_registration_id: string | null;
}

export function StudentResultVisibilityBrowser() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [semester, setSemester] = useState("All");
  const [selectedClass, setSelectedClass] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hideDialogOpen, setHideDialogOpen] = useState(false);
  const [hideReason, setHideReason] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  // Get distinct semesters from batches that have results
  const { data: semesters = [] } = useQuery({
    queryKey: ["visibility-browser-semesters"],
    queryFn: async () => {
      const { data } = await supabase
        .from("exam_batches")
        .select("semester")
        .in("status", ["approved", "verified", "published"]);
      return [...new Set((data ?? []).map((b) => b.semester))].sort();
    },
  });

  // Get classes for selected semester
  const { data: classes = [] } = useQuery({
    queryKey: ["visibility-browser-classes", semester],
    queryFn: async () => {
      let query = supabase
        .from("exam_batches")
        .select("class")
        .in("status", ["approved", "verified", "published"]);
      if (semester !== "All") query = query.eq("semester", semester);
      const { data } = await query;
      return [...new Set((data ?? []).map((b) => b.class))].sort();
    },
  });

  // Get student results grouped by form_iv_index
  const { data: students = [], isLoading } = useQuery({
    queryKey: ["visibility-browser-students", semester, selectedClass],
    queryFn: async () => {
      // Get all results, optionally filtered by semester
      let resultsQuery = supabase
        .from("exam_results")
        .select("form_iv_index, gpa, overall_remark, student_id, pre_registration_id, batch_id")
        .not("form_iv_index", "is", null);
      if (semester !== "All") resultsQuery = resultsQuery.eq("semester", semester);
      const { data: results } = await resultsQuery;

      if (!results || results.length === 0) return [];

      // Get batch IDs, optionally filtered by class/semester
      let batchQuery = supabase
        .from("exam_batches")
        .select("id")
        .in("status", ["approved", "verified", "published"]);
      if (selectedClass !== "All") batchQuery = batchQuery.eq("class", selectedClass);
      if (semester !== "All") batchQuery = batchQuery.eq("semester", semester);
      const { data: batches } = await batchQuery;

      const batchIds = new Set((batches ?? []).map((b) => b.id));
      const classResults = results.filter((r) => r.batch_id && batchIds.has(r.batch_id));

      // Group by form_iv_index
      const grouped = new Map<string, { gpa: string | null; overall_remark: string | null; student_id: string | null; pre_registration_id: string | null }>();
      for (const r of classResults) {
        if (!r.form_iv_index) continue;
        if (!grouped.has(r.form_iv_index)) {
          grouped.set(r.form_iv_index, {
            gpa: r.gpa,
            overall_remark: r.overall_remark,
            student_id: r.student_id,
            pre_registration_id: r.pre_registration_id,
          });
        }
      }

      if (grouped.size === 0) return [];

      // Fetch names from pre-registrations
      const indices = [...grouped.keys()];
      const { data: preRegs } = await (supabase as any)
        .from("student_pre_registrations_exam_safe")
        .select("form_iv_index, full_name, class")
        .in("form_iv_index", indices);

      const nameMap = new Map<string, { form_iv_index: string; full_name: string; class: string }>(
        (preRegs ?? []).map((p: any) => [p.form_iv_index, p])
      );

      const rows: StudentRow[] = indices.map((idx) => {
        const g = grouped.get(idx)!;
        const preReg = nameMap.get(idx);
        return {
          form_iv_index: idx,
          full_name: preReg?.full_name || "Unknown",
          class: preReg?.class || selectedClass,
          gpa: g.gpa,
          overall_remark: g.overall_remark,
          student_id: g.student_id,
          pre_registration_id: g.pre_registration_id,
        };
      });

      return rows.sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
  });

  // Get hidden rules for student scope
  const { data: hiddenRules = [] } = useQuery({
    queryKey: ["visibility-browser-hidden-rules"],
    queryFn: async () => {
      const { data } = await supabase
        .from("result_visibility_rules")
        .select("id, scope_value")
        .eq("scope_type", "student")
        .eq("visibility_status", "hidden");
      return data ?? [];
    },
  });

  // Fetch message templates for hide dialog
  const { data: messageTemplates = [] } = useQuery({
    queryKey: ["message-templates-visibility"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("message_templates")
        .select("id, title, body, category")
        .order("title");
      if (error) throw error;
      return data;
    },
  });

  const hiddenSet = useMemo(() => new Set(hiddenRules.map((r) => r.scope_value)), [hiddenRules]);

  // Filter and paginate
  const filtered = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter(
      (s) => s.full_name.toLowerCase().includes(q) || s.form_iv_index.toLowerCase().includes(q)
    );
  }, [students, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageStudents = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const allPageSelected = pageStudents.length > 0 && pageStudents.every((s) => selected.has(s.form_iv_index));

  const toggleSelect = (idx: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        pageStudents.forEach((s) => next.delete(s.form_iv_index));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pageStudents.forEach((s) => next.add(s.form_iv_index));
        return next;
      });
    }
  };

  const hideResultsMutation = useMutation({
    mutationFn: async ({ indices, reason }: { indices: string[]; reason: string }) => {
      if (!user) throw new Error("Not authenticated");
      const toHide = indices.filter((idx) => !hiddenSet.has(idx));
      if (toHide.length === 0) {
        toast({ title: "Already hidden", description: "Selected students' results are already hidden." });
        return;
      }
      const rows = toHide.map((idx) => ({
        scope_type: "student" as const,
        scope_value: idx,
        visibility_status: "hidden" as const,
        set_by: user.id,
        reason: reason || "Hidden via Student Result Visibility Browser",
      }));
      const { error } = await supabase.from("result_visibility_rules").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visibility-browser-hidden-rules"] });
      queryClient.invalidateQueries({ queryKey: ["result-visibility-rules"] });
      toast({ title: "Results hidden", description: `${selected.size} student(s) results are now hidden.` });
      setSelected(new Set());
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const showResultsMutation = useMutation({
    mutationFn: async (indices: string[]) => {
      const toShow = indices.filter((idx) => hiddenSet.has(idx));
      if (toShow.length === 0) {
        toast({ title: "Already visible", description: "Selected students' results are already visible." });
        return;
      }
      // Delete hidden rules for these students
      const ruleIds = hiddenRules.filter((r) => toShow.includes(r.scope_value)).map((r) => r.id);
      for (const id of ruleIds) {
        const { error } = await supabase.from("result_visibility_rules").delete().eq("id", id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visibility-browser-hidden-rules"] });
      queryClient.invalidateQueries({ queryKey: ["result-visibility-rules"] });
      toast({ title: "Results visible", description: `${selected.size} student(s) results are now visible.` });
      setSelected(new Set());
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const isMutating = hideResultsMutation.isPending || showResultsMutation.isPending;

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          Student Result Visibility Browser
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Browse students with uploaded results, then hide or show results individually or in bulk.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="w-36">
            <label className="text-xs text-muted-foreground mb-1 block">Semester</label>
            <Select value={semester} onValueChange={(v) => { setSemester(v); setSelectedClass("All"); setPage(0); setSelected(new Set()); }}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All" className="text-xs">All</SelectItem>
                {semesters.map((s) => <SelectItem key={s} value={s} className="text-xs">Semester {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <label className="text-xs text-muted-foreground mb-1 block">Class</label>
            <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setPage(0); setSelected(new Set()); }}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All" className="text-xs">All</SelectItem>
                {classes.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                className="h-8 text-xs pl-7"
                placeholder="Name or Form IV Index…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
            <span className="text-xs font-medium">{selected.size} selected</span>
            <Button
              size="sm"
              variant="destructive"
              className="h-7 text-xs gap-1"
              disabled={isMutating}
              onClick={() => { setHideReason(""); setSelectedTemplateId(""); setHideDialogOpen(true); }}
            >
              <EyeOff className="h-3 w-3" /> Hide Results
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              disabled={isMutating}
              onClick={() => showResultsMutation.mutate([...selected])}
            >
              <Eye className="h-3 w-3" /> Show Results
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        {false ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Select a semester and class to browse student results.
          </div>
        ) : isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-8 bg-muted rounded" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No students found with uploaded results for this selection.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox
                        checked={allPageSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="text-xs w-10">#</TableHead>
                    <TableHead className="text-xs">Student Name</TableHead>
                    <TableHead className="text-xs">Form IV Index</TableHead>
                    <TableHead className="text-xs">Class</TableHead>
                    <TableHead className="text-xs">GPA</TableHead>
                    <TableHead className="text-xs">Overall Remark</TableHead>
                    <TableHead className="text-xs">Visibility</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageStudents.map((s, i) => {
                    const isHidden = hiddenSet.has(s.form_iv_index);
                    const serial = page * PAGE_SIZE + i + 1;
                    return (
                      <TableRow key={s.form_iv_index} className={cn(selected.has(s.form_iv_index) && "bg-muted/40")}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(s.form_iv_index)}
                            onCheckedChange={() => toggleSelect(s.form_iv_index)}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{serial}</TableCell>
                        <TableCell className="text-xs font-medium">{s.full_name}</TableCell>
                        <TableCell className="text-xs">{s.form_iv_index}</TableCell>
                        <TableCell className="text-xs">{s.class}</TableCell>
                        <TableCell className="text-xs font-medium">{s.gpa || "—"}</TableCell>
                        <TableCell>
                          {s.overall_remark ? (
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px]",
                                s.overall_remark === "PASS" && "bg-emerald-500/10 text-emerald-700 border-emerald-200",
                                s.overall_remark === "SUPP" && "bg-amber-500/10 text-amber-700 border-amber-200",
                                s.overall_remark === "FAIL" && "bg-destructive/10 text-destructive border-destructive/20"
                              )}
                            >
                              {s.overall_remark}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isHidden ? (
                            <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20 gap-1">
                              <EyeOff className="h-2.5 w-2.5" /> Hidden
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-700 border-emerald-200 gap-1">
                              <Eye className="h-2.5 w-2.5" /> Visible
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} students
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-3 w-3" /> Previous
                </Button>
                <span className="text-xs px-2">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>

      {/* Hide Reason Dialog */}
      <Dialog open={hideDialogOpen} onOpenChange={setHideDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Hide Results — Select Reason
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DialogDescription className="text-xs">
              Choose a saved template or write a custom reason. This message will be shown to the student when they try to view their results.
            </DialogDescription>
            <div>
              <Label className="text-xs">Select Template</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={(v) => {
                  setSelectedTemplateId(v);
                  const tpl = messageTemplates.find((t: any) => t.id === v);
                  if (tpl) setHideReason(tpl.body);
                }}
              >
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Pick a template or write custom below…" />
                </SelectTrigger>
                <SelectContent>
                  {messageTemplates.map((t: any) => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">
                      {t.title} {t.category !== "general" && `(${t.category})`}
                    </SelectItem>
                  ))}
                  {messageTemplates.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No templates. Create one in User Management → SMS Templates.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason / Message</Label>
              <Textarea
                rows={4}
                className="text-xs"
                placeholder="The student will see this message…"
                value={hideReason}
                onChange={(e) => { setHideReason(e.target.value); setSelectedTemplateId(""); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setHideDialogOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={hideResultsMutation.isPending}
              onClick={() => {
                hideResultsMutation.mutate({ indices: [...selected], reason: hideReason });
                setHideDialogOpen(false);
              }}
            >
              <EyeOff className="h-3 w-3 mr-1" />
              {hideResultsMutation.isPending ? "Hiding..." : "Confirm Hide"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
